import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import AmountField from "../../Components/Form Fields/amount-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import renderEditMailingAddress from "../../Organisms/Modals/Mail Check/edit-mailing-address";
import renderAuthorizeMailCheck from "../../Organisms/Modals/Mail Check/authorize-mail-check";
import renderCheckFeeEducation from "../../Organisms/Modals/Mail Check/check-fee-education";

import { useActivities, useCheckConfig, useDeals, useUserAccount } from "../../CustomHooks/Queries";

import withFormProvider from "../../HOC/withFormProvider";

import { useModalContext } from "../../Contexts/ModalContext";

import { capitaliseString, dispatchErrorToast, formatCurrency } from "../../Utils/helpers";

import { buildAddressLines } from "./mail-check-utils";

import { ChevronLeft } from "../../Assets/Icons";

function MailACheck() {
  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { watch, setValue, handleSubmit } = useFormContext();

  const { isLoading: isLoadingActivities, data: activitiesData } = useActivities();

  const { isLoading: isLoadingUserAccountData, data: userAccountData } = useUserAccount();

  const { isLoading: isLoadingCheckConfig, data: checkConfigData } = useCheckConfig();

  const { isLoading: isLoadingDeals, data: dealsData } = useDeals();

  const availableBalance = activitiesData?.data?.data?.currentBalance ?? 0;

  const checkConfig = checkConfigData?.data ?? {};
  const cashout = dealsData?.data?.data?.currentDeal?.cashout ?? {};

  const checkFee = checkConfig.check_fees ?? 0;
  const checkMinAmount = checkConfig.check_min_amount ?? 0;
  const freeCheckEligible = !!checkConfig.free_check_eligible;
  const isFeeCharged = !!checkConfig.fee_applies && !freeCheckEligible;

  const mailingAddress = watch("mailingAddress");

  // Seed the mailing address from the member's account on first load.
  const hasSeededAddress = useRef(false);
  useEffect(() => {
    if (hasSeededAddress.current || isLoadingUserAccountData) return;
    hasSeededAddress.current = true;
    const user = userAccountData?.data?.data?.user ?? {};
    const seeded = {
      firstName: capitaliseString(user?.firstName) ?? "",
      lastName: capitaliseString(user?.lastName) ?? "",
      street: user?.address1 ?? "",
      apt: user?.address2 ?? "",
      city: user?.city ?? "",
      state: user?.state ?? "",
      zip: user?.zipCode ?? "",
    };
    setValue("mailingAddress", seeded);
  }, [isLoadingUserAccountData, userAccountData, setValue]);

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");

  const recipientName = useMemo(() => {
    if (!mailingAddress) return "";
    return `${mailingAddress.firstName ?? ""} ${mailingAddress.lastName ?? ""}`.trim();
  }, [mailingAddress]);

  const addressLines = useMemo(() => buildAddressLines(mailingAddress), [mailingAddress]);

  const disableSubmitButton = isNaN(cashAmount) || cashAmount === 0 || !mailingAddress;

  const onEditAddress = () =>
    renderEditMailingAddress(modalContext, {
      address: mailingAddress,
      onSave: (updatedAddress) => setValue("mailingAddress", updatedAddress),
    });

  const onSubmit = () => {
    if (cashAmount < checkMinAmount) {
      dispatchErrorToast(`Minimum amount for check is not met. Minimum is ${formatCurrency(checkMinAmount)}.`, {
        alignToAppContent: true,
        key: "amountBelowCheckMinimum",
      });
      return;
    }

    if (cashAmount > availableBalance) {
      dispatchErrorToast("Amount exceeds what you have available.", {
        alignToAppContent: true,
        key: "amountExceedsAvailableFunds",
      });
      return;
    }

    if (checkConfig.fee_applies) {
      renderCheckFeeEducation(modalContext, {
        cashAmount,
        mailingAddress,
        checkFee,
        freeCheckEligible,
        freeCheckAllowance: checkConfig.free_check_allowance ?? 1,
        freeCheckCooldownMonths: checkConfig.free_check_cooldown_months,
        isBankAllowed: !!cashout.is_external_bank_allowed,
        isVenmoAllowed: !!cashout.is_venmo_allowed,
      });
      return;
    }

    renderAuthorizeMailCheck(modalContext, { cashAmount, mailingAddress, checkFee, isFeeCharged });
  };

  const isLoading = isLoadingActivities || isLoadingUserAccountData || isLoadingCheckConfig || isLoadingDeals;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  return (
    <div className={`mail-check${animReady ? " anim-ready" : ""}`}>
      <div className="mc-header">
        <button className="mc-back" onClick={navigate.bind(this, -1)} type="button" aria-label="Go back">
          <ChevronLeft />
        </button>
        <h1 className="mc-title">Mail a Check</h1>
      </div>

      {isLoading ? (
        <div className="mc-loading">
          <CircularProgress />
        </div>
      ) : (
        <form className="mc-body" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="mc-amount-section">
            <AmountField />
            <span className="mc-available-pill">{formatCurrency(availableBalance)} available</span>
          </div>

          <div className="mc-transfer-flow">
            <div className="mc-endpoint-card">
              <span className="mc-endpoint-label">From</span>
              <div className="mc-endpoint-icon mc-stake-icon">
                <span>S</span>
              </div>
              <span className="mc-endpoint-name">Stake Wallet</span>
              <span className="mc-endpoint-detail">{formatCurrency(availableBalance)} available</span>
            </div>

            <svg className="mc-arrow" width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M4 10H16" stroke="#636879" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12 6L16 10L12 14" stroke="#636879" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            <div className="mc-endpoint-card">
              <span className="mc-endpoint-label">To</span>
              <div className="mc-endpoint-icon mc-check-icon">
                <svg width="20" height="16" viewBox="0 0 24 18" fill="none">
                  <rect x="1.5" y="3" width="21" height="12" rx="1.5" fill="#FFFFFF" stroke="#FFFFFF" strokeWidth="0.8" />
                  <line x1="1.5" y1="6.5" x2="22.5" y2="6.5" stroke="#1E2231" />
                  <line x1="4" y1="10" x2="10" y2="10" stroke="#1E2231" strokeWidth="1.2" strokeLinecap="round" />
                  <line x1="4" y1="12" x2="8" y2="12" stroke="#1E2231" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </div>
              <span className="mc-endpoint-name">Paper check</span>
              <span className="mc-endpoint-detail">Mailed by USPS</span>
            </div>
          </div>

          <div className="mc-address-card">
            <div className="mc-address-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"
                  fill="none"
                  stroke="#54FF99"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="10" r="3" fill="none" stroke="#54FF99" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="mc-address-info">
              <span className="mc-address-label">Mail to</span>
              <span className="mc-address-name">{recipientName || "Add a recipient"}</span>
              <span className="mc-address-lines">
                {addressLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </span>
            </div>
            <button type="button" className="mc-edit-pill" onClick={onEditAddress}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="#54FF99" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="#54FF99" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Edit
            </button>
          </div>

          <div className="mc-details-card">
            <div className="mc-detail-row">
              <span className="mc-detail-label">Fee{isFeeCharged && " (deducted from amount)"}</span>
              <span className={isFeeCharged ? "mc-detail-value-warning" : "mc-detail-value-accent"}>
                {isFeeCharged ? formatCurrency(checkFee) : "Free"}
              </span>
            </div>
            <div className="mc-detail-divider" />
            <div className="mc-detail-row">
              <span className="mc-detail-label">Arrives by mail</span>
              <span className="mc-detail-value">7–10 business days</span>
            </div>
          </div>

          <PrimaryButton className="mc-submit-btn" type="submit" disabled={disableSubmitButton}>
            Continue
          </PrimaryButton>
        </form>
      )}
    </div>
  );
}

export default withFormProvider(MailACheck);
